import type { Axios } from "axios";

import { Backend, getBackend } from "../../api/apiConfig";
import type {
  DataGridFilter,
  FilterLogicalOperator,
} from "../../utils/backendFilters";
import { stringifyValues } from "../../utils/utils";
import { getClaimsProceduresAndPatient } from "../ClaimsDashboard/api";

export interface PaymentsSearchParams {
  page: number;
  pageSize: number;
  filters: Record<string, (string | null)[]>;
  datagridFilters?: DataGridFilter[];
  logicalOperator?: FilterLogicalOperator;
  devMode: boolean;
}

export interface PaymentDetail {
  payment: PaymentDetailMessage;
  claims: ClaimWithProcedureAndPatientMessage[];
}

const reviewStatusFilter = (devMode: boolean): string[] | undefined => {
  if (!devMode) {
    return ["ACCEPTED", "BYPASS"];
  }
  if (getBackend() === Backend.Production) {
    return ["ACCEPTED", "BYPASS", "REVIEW", "MODIFIED"];
  }
  return undefined;
};

export const getPayments = (
  c: Axios,
  params: PaymentsSearchParams,
  signal?: AbortSignal,
) => {
  const filters: Record<string, (string | null)[]> = {
    payment_type:
      getBackend() === Backend.Production
        ? [null, "EFT"]
        : [null, "CHECK", "VIRTUAL_CARD", "EFT"],
    payment_status: [null, "Cleared"],
    ...params.filters,
  };
  const reviewStatus = reviewStatusFilter(params.devMode);
  if (reviewStatus) {
    filters.review_status = reviewStatus;
  }
  const requestBody: {
    filters: Record<string, (string | null)[]>;
    page: number;
    page_size: number;
    sort: string[];
    sort_direction: string;
    datagrid_filters?: DataGridFilter[];
    logical_operator?: string;
  } = {
    filters,
    page: params.page,
    page_size: params.pageSize,
    sort: [],
    sort_direction: "desc",
  };
  if (params.datagridFilters && params.datagridFilters.length > 0) {
    requestBody.datagrid_filters = params.datagridFilters;
  }
  if (params.logicalOperator) {
    requestBody.logical_operator = params.logicalOperator;
  }

  const queryParams = new URLSearchParams();
  queryParams.append("dev_mode", String(params.devMode));

  return c
    .post<SearchResponse>(
      `/payments/search?${queryParams.toString()}`,
      requestBody,
      { signal },
    )
    .then((r) => r.data);
};

export const getPaymentDetail = (
  c: Axios,
  paymentId: string,
  devMode: boolean,
  signal?: AbortSignal,
) =>
  c
    .get<PaymentDetailMessage>(`/payments/${paymentId}?dev_mode=${devMode}`, {
      signal,
    })
    .then((r) => r.data);

export const getPaymentWithClaims = async (
  c: Axios,
  paymentId: string,
  devMode: boolean,
  signal?: AbortSignal,
): Promise<PaymentDetail> => {
  const payment = await getPaymentDetail(c, paymentId, devMode, signal);
  // Re-fetch through the claims search endpoint to get denial fields
  const claimIds = (payment.Claim ?? []).map((claim) => claim.wieldyId);
  const claims = claimIds.length
    ? (
        await getClaimsProceduresAndPatient(
          c,
          {
            filters: { filters: { wieldy_id: claimIds } },
            tab: "",
            page: 1,
            pageSize: 500,
            sortField: undefined,
            sortDirection: "desc",
            view: null,
            deniseQueue: null,
            devMode,
          },
          signal,
        )
      ).data
    : [];
  return { payment, claims };
};

const postUpdate = async (
  c: Axios,
  body: Record<string, unknown>,
): Promise<string> => {
  const { search } = window.location;
  const response = await c.post(`updates${search}`, body);
  return response.data.wieldyId;
};

export const createPayment = (c: Axios, payment: PaymentMessage) =>
  postUpdate(c, {
    type: "Payment",
    payload: stringifyValues(payment),
    verb: "CREATE",
  });

export const updatePayment = (c: Axios, payment: Partial<PaymentMessage>) =>
  postUpdate(c, {
    type: "Payment",
    payload: stringifyValues(payment),
    verb: "UPDATE",
  });

export const createAIEntity = async (
  c: Axios,
  aiMessage: AIMessage,
  fileId: string,
): Promise<void> => {
  const response = await c.post(`parses/files/${fileId}`, {
    practiceId: aiMessage.practiceId,
    source: aiMessage.source,
    payer: aiMessage.payer,
    dataFormat: "PDF",
    documentLevelType: aiMessage.documentLevelType,
  });
  return response.data.wieldyId;
};
